// قواعد الملفات في الواجهة: JPG / JPEG / PNG / PDF فقط (HEIC غير معروض كصيغة مسموحة).
// الخادم يفحص النوع من محتوى الملف نفسه؛ هذه الفحوص لتوفير رحلة فاشلة على البياع لا أكثر.
export const ACCEPT_ATTR = '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf';
export const ALLOWED_MIME = ['image/jpeg', 'image/png', 'application/pdf'] as const;
export const ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'pdf'] as const;
export const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXT_SET = new Set<string>(ALLOWED_EXT);
export const FORMATS_HINT = 'الصيغ المسموحة: JPG · JPEG · PNG · PDF — بحد أقصى 10 ميجابايت للملف';

const ext = (name: string): string => (name.split('.').pop() ?? '').toLowerCase();

/** يعيد رسالة خطأ عربية، أو null إذا كان الملف مقبولًا. */
export function validateFile(file: { name: string; size: number; type: string }): string | null {
  if (!file.size) return 'الملف فارغ';
  if (file.size > MAX_BYTES) return `حجم الملف ${humanSize(file.size)} — الحد الأقصى 10 ميجابايت`;
  const e = ext(file.name);
  if (!ALLOWED_EXT_SET.has(e)) {
    return e === 'heic' || e === 'heif'
      ? 'صيغة HEIC غير مدعومة — احفظ الصورة بصيغة JPG أو PNG ثم أعد الرفع'
      : 'صيغة غير مسموحة — المسموح: JPG · JPEG · PNG · PDF';
  }
  if (file.type && !(ALLOWED_MIME as readonly string[]).includes(file.type)) {
    return 'صيغة غير مسموحة — المسموح: JPG · JPEG · PNG · PDF';
  }
  return null;
}

export function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} بايت`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} كيلوبايت`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} ميجابايت`;
}

/** فئات مرفقات الحجز كما يقبلها الخادم (communication_category). «أخرى» تتطلب وصفًا. */
export const ATTACHMENT_CATEGORIES = [
  { code: 'CHAT_SCREENSHOT', ar: 'محادثة مع العميل' },
  { code: 'CANCELLATION_MODIFICATION_POLICY', ar: 'سياسة الإلغاء / التعديل' },
  { code: 'TICKET_POLICY_ACKNOWLEDGEMENT', ar: 'إقرار سياسة التذكرة' },
  // صنفان يلزمان نوعَي التعديل والإلغاء بعينهما (0090) — فيُعرف أن المطلوب رُفع
  { code: 'MODIFICATION_TRANSFER_PROOF', ar: 'تحويل العميل مقابل التعديل' },
  { code: 'SUPPORT_CHAT_SCREENSHOT', ar: 'محادثة الدعم (الغرامة / المسترد)' },
  // الأكثر رفعًا باسمه لا تحت «أخرى» بوصفٍ يُكتب كل مرّة (0118، قرار المالك 23/09/2026)
  { code: 'FLIGHT_TICKET', ar: 'تذكرة الطيران' },
  { code: 'PASSPORT', ar: 'جواز السفر' },
  // «تصفية حساب الحجز» (0137، قرار المالك 01/10/2026) — له قسمه المستقلّ في شاشة الحجز، ولا يظهر في قائمة الفئات
  { code: 'ACCOUNT_SETTLEMENT', ar: 'تصفية حساب الحجز' },
  { code: 'OTHER', ar: 'مستند آخر متعلق بالحجز' },
] as const;

export const categoryAr = (code: string): string =>
  ATTACHMENT_CATEGORIES.find(c => c.code === code)?.ar ?? code;

/**
 * ما الذي يُرفَع هنا عادةً — **ملاحظة إرشادية فقط**.
 *
 * لا تفرض شيئًا ولا تُقيِّد صيغة ولا تمنع رفعًا: هي تذكيرٌ بالمستندات التي يحتاجها الملف عادةً
 * حتى لا ينساها البياع. قواعد القبول التقنية لم تتغيّر ولا حرف — انظر `validateFile` أعلاه.
 */
export const ATTACHMENT_PURPOSE_ITEMS = [
  'إقرار المراجعة', 'التذكرة', 'جواز السفر', 'شات العميل للإلغاء أو التعديل', 'التأشيرة',
] as const;

/**
 * ونفس القائمة جملةً واحدة — تبقى للنصّ المكتوب حيث لا شارات (والشارات مبنيّةٌ منها،
 * فلا تفترق قائمتان في ذهن من يقرأ إحداهما).
 */
export const ATTACHMENTS_PURPOSE_NOTE =
  `مرفقات الحجز: ${ATTACHMENT_PURPOSE_ITEMS.slice(0, -1).join('، ')}، و${ATTACHMENT_PURPOSE_ITEMS.at(-1)}.`;

/** وظيفة القسم في جملة واحدة — تُعرض قبل قائمة المستندات. */
export const ATTACHMENTS_SECTION_NOTE = 'مرفقات الحجز للتوثيق العام.';

/**
 * أين يُرفع إثبات الدفع — **ليست قاعدة منع**.
 *
 * مكانها تحت «يمكنك حفظ الحجز بلا دفعة» في شاشة الحجز، لا داخل قسم المرفقات: قسم المرفقات
 * للتوثيق العام ويقبل ما يخصّ الملف، وإثبات الدفعة يخصّ الدفعة نفسها. وخلطُ الجملتين كان
 * يوحي بأن قسم المرفقات يمنع رفع إثبات — وهو لا يمنع شيئًا.
 */
export const PAYMENT_EVIDENCE_LOCATION_NOTE =
  'إثبات الدفع الذي يعتمد عليه الأدمن للتحقق من الدفعة يُرفع من داخل الدفعة نفسها.';

/** سياسة الاحتفاظ: يُحذف محتوى الملف تلقائيًا بعد 6 أشهر من تاريخ رفعه. */
export const RETENTION_EXPIRED_NOTE = 'تم حذف الملف تلقائيًا بعد انتهاء مدة الاحتفاظ 6 أشهر';
export const retentionHint = (expiresOn: string | null | undefined): string | null =>
  expiresOn ? `يُحذف محتوى الملف تلقائيًا في ${expiresOn}` : null;
export const isContentDeleted = (a: { contentDeletedAt?: string | null }): boolean => !!a.contentDeletedAt;
